import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Boxes,
  ChefHat,
  CreditCard,
  Croissant,
  LayoutGrid,
  LogOut,
  MessagesSquare,
  Moon,
  MoreHorizontal,
  Settings as SettingsIcon,
  ShieldAlert,
  ShoppingBag,
  Sun,
  Users,
} from 'lucide-react';
import type { Permission } from '@bakery/shared';

import { useVerticalSwipe } from './lib/swipe.js';
import { useLiveUpdates } from './lib/useLive.js';

import { api } from './lib/api.js';
import { useAuth } from './lib/auth.js';
import { Button, EmptyState, cx } from './components/ui.js';
import { AuthLoading, ChangePasswordScreen, SignInScreen } from './screens/SignIn.js';
import { DashboardScreen } from './screens/Dashboard.js';
import { RegisterScreen } from './screens/Register.js';
import { OrdersScreen } from './screens/Orders.js';
import { CustomersScreen } from './screens/Customers.js';
import { InboxScreen } from './screens/Inbox.js';
import { InventoryScreen } from './screens/Inventory.js';
import { CatalogScreen } from './screens/Catalog.js';
import { StaffScreen } from './screens/Staff.js';
import { SettingsScreen } from './screens/Settings.js';

interface NavItem {
  to: string;
  label: string;
  short: string;
  icon: typeof LayoutGrid;
  /** Hidden entirely when the signed-in user lacks this. */
  permission?: Permission;
  /** Shown in the bottom bar on phones. */
  primary?: boolean;
}

const NAV: NavItem[] = [
  { to: '/register', label: 'Register', short: 'Sell', icon: CreditCard, permission: 'pos.sell', primary: true },
  { to: '/orders', label: 'Orders', short: 'Orders', icon: ShoppingBag, permission: 'orders.view', primary: true },
  { to: '/inbox', label: 'Inbox', short: 'Inbox', icon: MessagesSquare, permission: 'inbox.view', primary: true },
  { to: '/customers', label: 'Clients', short: 'Clients', icon: Users, permission: 'customers.view', primary: true },
  { to: '/', label: 'Dashboard', short: 'Home', icon: BarChart3, permission: 'reports.revenue', primary: true },
  { to: '/inventory', label: 'Stock room', short: 'Stock', icon: Boxes, permission: 'inventory.view' },
  { to: '/catalog', label: 'Products & recipes', short: 'Menu', icon: Croissant, permission: 'products.view' },
  { to: '/staff', label: 'Team & payroll', short: 'Team', icon: ChefHat, permission: 'staff.view' },
  { to: '/settings', label: 'Settings', short: 'Settings', icon: SettingsIcon },
];

function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('bakery.theme') as 'light' | 'dark') ?? 'light',
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('bakery.theme', theme);
  }, [theme]);

  return { theme, toggle: () => setTheme((current) => (current === 'light' ? 'dark' : 'light')) };
}

/**
 * Badge counts for the navigation.
 *
 * Each query is disabled unless the user may read it, so a staff account never
 * fires a request it would only get a 403 from.
 */
function useNavCounts() {
  const { can } = useAuth();

  const { data: buckets } = useQuery({
    queryKey: ['orders', 'buckets'],
    queryFn: api.orders.buckets,
    refetchInterval: 60_000,
    enabled: can('orders.view'),
  });
  const { data: inbox } = useQuery({
    queryKey: ['conversations', 'counts'],
    queryFn: () => api.inbox.conversations({ limit: '1' }),
    refetchInterval: 30_000,
    enabled: can('inbox.view'),
  });
  const { data: alerts } = useQuery({
    queryKey: ['stock-alerts'],
    queryFn: () => api.inventory.alerts(),
    enabled: can('inventory.view'),
  });

  const dueNow = (buckets ?? [])
    .filter((b) => b.bucket === 'today' || b.bucket === 'overdue')
    .reduce((sum, b) => sum + b.count, 0);

  return {
    '/orders': dueNow,
    '/inbox': inbox?.counts.open ?? 0,
    '/inventory': (alerts ?? []).filter((a) => a.level !== 'low').length,
  } as Record<string, number>;
}

/** Route wrapper. The server refuses too; this just avoids a broken screen. */
function Guarded({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { can } = useAuth();
  if (can(permission)) return <>{children}</>;

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <EmptyState
        icon={<ShieldAlert className="size-8" />}
        title="Not available to your account"
        description="Ask the shop owner if you need access to this."
      />
    </div>
  );
}

export function App() {
  const { user, loading, mustChangePassword } = useAuth();

  if (loading) return <AuthLoading />;
  if (!user) return <SignInScreen />;
  if (mustChangePassword) return <ChangePasswordScreen />;

  return <Shell />;
}

function Shell() {
  const { theme, toggle } = useTheme();
  const { user, can, signOut } = useAuth();
  const counts = useNavCounts();
  const location = useLocation();

  // One connection for the whole app: a message must be heard on the Register,
  // not only by whoever happens to have the Inbox open.
  useLiveUpdates(can('inbox.view'));
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: api.settings.get });

  const shopName = settings?.business.tradingName ?? "ANA's BAKERY";
  const visible = NAV.filter((item) => !item.permission || can(item.permission));
  /* Everything the phone's bar has no room for. Reached through "More". */
  const secondary = visible.filter((item) => !item.primary);
  const [moreOpen, setMoreOpen] = useState(false);

  // A tap through the menu should leave it closed behind you.
  useEffect(() => setMoreOpen(false), [location.pathname]);

  // Someone without the dashboard lands on the register instead of a wall.
  const home = can('reports.revenue') ? '/' : (visible[0]?.to ?? '/settings');

  return (
    <div className="flex h-full flex-col lg:flex-row">
      {/* Desktop / tablet rail ------------------------------------------- */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-[var(--color-line)] bg-[var(--color-surface)] lg:flex">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <Logo />
          <div className="min-w-0">
            <p className="truncate font-display text-sm font-semibold">{shopName}</p>
            <p className="truncate text-xs text-[var(--color-ink-faint)]">
              {settings?.business.city ?? 'Taguig'}
            </p>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 px-3 py-2">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-medium transition',
                  isActive
                    ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]'
                    : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]',
                )
              }
            >
              <item.icon className="size-[18px] shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
              {(counts[item.to] ?? 0) > 0 && (
                <span className="tabular rounded-full bg-crust-600 px-1.5 py-0.5 text-[11px] leading-none text-white">
                  {counts[item.to]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-1 border-t border-[var(--color-line)] p-3">
          <div className="flex items-center gap-2.5 px-3 py-2">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[var(--color-accent-soft)] text-xs font-semibold text-[var(--color-accent-ink)]">
              {user?.displayName.slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium">{user?.displayName}</p>
              <p className="truncate text-[11px] text-[var(--color-ink-faint)]">
                {user?.role === 'admin' ? 'Administrator' : 'Staff'}
              </p>
            </div>
          </div>

          <button
            onClick={toggle}
            className="flex w-full items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-sm font-medium text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-sunken)]"
          >
            {theme === 'light' ? <Moon className="size-[18px]" /> : <Sun className="size-[18px]" />}
            {theme === 'light' ? 'Dark' : 'Light'} mode
          </button>

          <button
            onClick={() => void signOut()}
            className="flex w-full items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-sm font-medium text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-sunken)]"
          >
            <LogOut className="size-[18px]" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Phone header ------------------------------------------------------ */}
      <header className="flex items-center justify-between border-b border-[var(--color-line)] bg-[var(--color-surface)] px-4 py-3 pt-safe lg:hidden">
        <div className="flex items-center gap-2">
          <Logo small />
          <span className="font-display text-sm font-semibold">
            {NAV.find((item) => item.to === location.pathname)?.label ?? shopName}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggle} aria-label="Toggle theme" className="text-[var(--color-ink-soft)]">
            {theme === 'light' ? <Moon className="size-5" /> : <Sun className="size-5" />}
          </button>
          <button onClick={() => void signOut()} aria-label="Sign out" className="text-[var(--color-ink-soft)]">
            <LogOut className="size-5" />
          </button>
        </div>
      </header>

      <main className="min-w-0 flex-1 overflow-y-auto pb-tabbar lg:pb-0">
        <Routes>
          <Route
            path="/"
            element={
              can('reports.revenue') ? <DashboardScreen /> : <Navigate to={home} replace />
            }
          />
          <Route path="/register" element={<Guarded permission="pos.sell"><RegisterScreen /></Guarded>} />
          <Route path="/orders" element={<Guarded permission="orders.view"><OrdersScreen /></Guarded>} />
          <Route path="/orders/:orderId" element={<Guarded permission="orders.view"><OrdersScreen /></Guarded>} />
          <Route path="/inbox" element={<Guarded permission="inbox.view"><InboxScreen /></Guarded>} />
          <Route path="/inbox/:conversationId" element={<Guarded permission="inbox.view"><InboxScreen /></Guarded>} />
          <Route path="/customers" element={<Guarded permission="customers.view"><CustomersScreen /></Guarded>} />
          <Route path="/customers/:customerId" element={<Guarded permission="customers.view"><CustomersScreen /></Guarded>} />
          <Route path="/inventory" element={<Guarded permission="inventory.view"><InventoryScreen /></Guarded>} />
          <Route path="/catalog" element={<Guarded permission="products.view"><CatalogScreen /></Guarded>} />
          <Route path="/staff" element={<Guarded permission="staff.view"><StaffScreen /></Guarded>} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </main>

      {/*
        Phone tab bar.

        "More" is not decoration. The bar shows only the `primary` items and the
        sidebar beside it is `lg:` and up, so on a phone the Stock room,
        Products & recipes, Team & payroll and Settings had no entry point at
        all — not hidden behind a scroll, simply unreachable. That is the same
        hole that made "Settings → This device" impossible to open on the first
        Android build, which was worked around at the sign-in screen rather than
        fixed here.
      */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex h-tabbar border-t border-[var(--color-line)] bg-[var(--color-surface)] pb-safe lg:hidden">
        {visible
          .filter((item) => item.primary)
          .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cx(
                  'relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium',
                  isActive ? 'text-crust-600' : 'text-[var(--color-ink-faint)]',
                )
              }
            >
              <item.icon className="size-5" />
              {item.short}
              {(counts[item.to] ?? 0) > 0 && (
                <span className="tabular absolute top-1.5 right-[22%] rounded-full bg-crust-600 px-1 text-[10px] leading-tight text-white">
                  {counts[item.to]}
                </span>
              )}
            </NavLink>
          ))}

        {secondary.length > 0 && (
          <button
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            className={cx(
              'relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium',
              secondary.some((item) => item.to === location.pathname)
                ? 'text-crust-600'
                : 'text-[var(--color-ink-faint)]',
            )}
          >
            <MoreHorizontal className="size-5" />
            More
          </button>
        )}
      </nav>

      {moreOpen && (
        <MoreMenu items={secondary} counts={counts} onClose={() => setMoreOpen(false)} />
      )}
    </div>
  );
}

/**
 * The rest of the app, on a phone.
 *
 * A sheet rather than a full screen: these are destinations, and covering the
 * register to list them would make choosing one feel like leaving what you were
 * doing. Swipe it down or tap outside to dismiss.
 */
function MoreMenu({
  items,
  counts,
  onClose,
}: {
  items: NavItem[];
  counts: Record<string, number>;
  onClose: () => void;
}) {
  const swipe = useVerticalSwipe({ onSwipeDown: onClose });

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:hidden" role="dialog" aria-modal="true" aria-label="More">
      <div className="absolute inset-0 bg-crust-900/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden />

      <div className="relative w-full rounded-t-[var(--radius-xl)] bg-[var(--color-surface)] pb-tabbar elevation-overlay">
        {/* The drag surface: not scrollable, so the browser will not claim the gesture. */}
        <div {...swipe} className="touch-none px-5 pt-3 pb-1">
          <div className="mx-auto h-1 w-9 rounded-full bg-[var(--color-line-strong)]" aria-hidden />
          <p className="mt-3 font-display text-base font-semibold">More</p>
        </div>

        <ul className="px-2 pb-3">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  cx(
                    'flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-3 text-sm font-medium',
                    isActive ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]' : 'text-[var(--color-ink)]',
                  )
                }
              >
                <item.icon className="size-5 shrink-0 text-[var(--color-ink-soft)]" />
                <span className="flex-1">{item.label}</span>
                {(counts[item.to] ?? 0) > 0 && (
                  <span className="tabular rounded-full bg-crust-600 px-1.5 text-[11px] leading-tight text-white">
                    {counts[item.to]}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** The wheat-and-oven mark, drawn to echo the bakery's circular logo badge. */
function Logo({ small }: { small?: boolean }) {
  return (
    <span
      className={cx(
        'grid shrink-0 place-items-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]',
        small ? 'size-8' : 'size-10',
      )}
      aria-hidden
    >
      <svg viewBox="0 0 24 24" className={small ? 'size-4' : 'size-5'} fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M12 3v18" strokeLinecap="round" />
        <path d="M12 6.5c-1.6-1.4-3.4-1.6-4.6-1.4-.2 1.2 0 3 1.4 4.6 1.4 1.4 3.2 1.4 3.2 1.4M12 6.5c1.6-1.4 3.4-1.6 4.6-1.4.2 1.2 0 3-1.4 4.6-1.4 1.4-3.2 1.4-3.2 1.4" strokeLinejoin="round" />
        <path d="M12 12.5c-1.6-1.4-3.4-1.6-4.6-1.4-.2 1.2 0 3 1.4 4.6 1.4 1.4 3.2 1.4 3.2 1.4M12 12.5c1.6-1.4 3.4-1.6 4.6-1.4.2 1.2 0 3-1.4 4.6-1.4 1.4-3.2 1.4-3.2 1.4" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

export { Button };
